import React, { useCallback, useEffect, useState } from 'react';
import { BugOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { ozonEventsAPI } from '@/api/endpoints/ozonEvents.js';
import styles from '@/styles/pages/ozonEvents/OzonEventsPage.module.css';

const REFRESH_INTERVAL_MS = 10000;
const PAGE_SIZE = 100;
const LEVELS = ['info', 'warning', 'error', 'critical'];

const formatDate = (s) =>
  new Date(s).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

const OzonEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [level, setLevel] = useState('');
  const [browser, setBrowser] = useState('');
  const [event, setEvent] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    document.title = 'Ozon события | DevSpace';
  }, []);

  const buildParams = useCallback(() => {
    const p = { limit: PAGE_SIZE };
    if (level) p.level = level;
    if (browser.trim()) p.browser = browser.trim();
    if (event.trim()) p.event = event.trim();
    return p;
  }, [level, browser, event]);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    const response = await ozonEventsAPI.getEvents(buildParams());
    if (response.success) {
      setEvents(response.data || []);
      setHasMore((response.data || []).length === PAGE_SIZE);
      setError(null);
    } else {
      setError(response.error || 'Не удалось загрузить события');
    }
    if (showSpinner) setLoading(false);
  }, [buildParams]);

  const loadMore = async () => {
    if (!events.length) return;
    const response = await ozonEventsAPI.getEvents({ ...buildParams(), before_id: events[events.length - 1].id });
    if (response.success) {
      const next = response.data || [];
      setEvents((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    }
  };

  useEffect(() => {
    load(true);
    const interval = setInterval(() => load(false), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <BugOutlined /> Ozon события
          </h1>
          <button className={styles.refreshButton} onClick={() => load(true)}>
            <ReloadOutlined /> Обновить
          </button>
        </div>

        <div className={styles.filters}>
          <select value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">Все уровни</option>
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <input placeholder="Браузер" value={browser} onChange={(e) => setBrowser(e.target.value)} />
          <input placeholder="Событие (browser.crashed…)" value={event} onChange={(e) => setEvent(e.target.value)} />
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <div className={styles.list}>
          {loading ? (
            <div className={styles.empty}><LoadingOutlined /> Загрузка...</div>
          ) : events.length === 0 ? (
            <div className={styles.empty}>Событий нет</div>
          ) : (
            events.map((e) => (
              <div
                key={e.id}
                className={`${styles.row} ${styles[e.level] || ''}`}
                onClick={() => setExpanded(expanded === e.id ? null : e.id)}
              >
                <div className={styles.rowMain}>
                  <span className={styles.time}>{formatDate(e.created_at)}</span>
                  <span className={styles.badge}>{e.level}</span>
                  <span className={styles.eventName}>{e.event}</span>
                  {e.browser && <span className={styles.browser}>#{e.browser}</span>}
                  <span className={styles.message}>{e.message}</span>
                </div>
                {expanded === e.id && (
                  <pre className={styles.data}>
                    {JSON.stringify({ source: e.source, sent_at: e.event_ts, ...e.data }, null, 2)}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>

        {hasMore && !loading && (
          <button className={styles.moreButton} onClick={loadMore}>Загрузить ещё</button>
        )}
      </div>
    </div>
  );
};

export default OzonEventsPage;
